/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import { Box } from "@material-ui/core";
import { SPView } from "components/common/MediaQuery";
import { streamingActions } from "modules/streaming";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { StreamingIconButton } from "ui/StreamingIconButton";
import { StreamingIconButtonWithText } from "ui/StreamingIconButtonWithText";
import { createXShareLink } from "utility/createXShareLink";
import { pushDataLayerWithClientBuildUnixTime } from "utility/ga";

import { AnnouncementIcon } from "./AnnouncementIcon";
import { VoteIcon } from "./VoteIcon";

import type { FC, SVGAttributes } from "react";
import type { Store } from "store";

export const StreamingOptions: FC<{
  showAnnouncement: boolean;
  isLiveEventEnabled: boolean;
  handleToggleAnnouncement: () => void;
  handleToggleLiveEvent: () => void;
}> = ({
  showAnnouncement,
  isLiveEventEnabled,
  handleToggleAnnouncement,
  handleToggleLiveEvent,
}) => {
  const dispatch = useDispatch();

  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const eventInfo = useSelector((state: Store) => state.event.displayEvent);

  const twitterLink = createXShareLink({
    eventId: eventInfo.eventId,
    eventTitle: eventInfo.eventTitle,
    twitterHashTag: eventInfo.twitterHashTag,
  });

  const handleToggleDarkMode = useCallback(() => {
    dispatch(streamingActions.toggleDarkMode());
  }, [dispatch]);

  const handleShareClick = useCallback(() => {
    pushDataLayerWithClientBuildUnixTime({
      event: "share",
      method: "X",
      content_type: "event",
      item_id: eventInfo.eventId,
      eid: eventInfo.eventId,
    });
  }, [eventInfo.eventId]);

  return (
    <StreamingOptionsView
      isDarkModeEnabled={isDarkModeEnabled}
      twitterLink={twitterLink}
      showAnnouncement={showAnnouncement}
      isLiveEventEnabled={isLiveEventEnabled}
      handleToggleDarkMode={handleToggleDarkMode}
      handleShareClick={handleShareClick}
      handleToggleAnnouncement={handleToggleAnnouncement}
      handleToggleLiveEvent={handleToggleLiveEvent}
    />
  );
};

type StreamingOptionsViewProps = {
  isDarkModeEnabled: boolean;
  twitterLink: string;
  showAnnouncement: boolean;
  isLiveEventEnabled: boolean;
  handleToggleDarkMode: () => void;
  handleShareClick: () => void;
  handleToggleAnnouncement: () => void;
  handleToggleLiveEvent: () => void;
};
export const StreamingOptionsView: FC<StreamingOptionsViewProps> = ({
  isDarkModeEnabled,
  twitterLink,
  showAnnouncement,
  isLiveEventEnabled,
  handleToggleDarkMode,
  handleShareClick,
  handleToggleAnnouncement,
  handleToggleLiveEvent,
}) => {
  return (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Box display={"flex"} alignItems={"center"} gridGap={"8px"}>
        <StreamingIconButton
          isDarkModeEnabled={isDarkModeEnabled}
          onClick={handleToggleDarkMode}
        >
          <DarkModeIcon
            style={{
              width: "18px",
              height: "auto",
            }}
          />
        </StreamingIconButton>
      </Box>

      <Box display={"flex"} alignItems={"center"} gridGap={"8px"}>
        {isLiveEventEnabled && (
          <SPView>
            <StreamingIconButton
              isDarkModeEnabled={isDarkModeEnabled}
              onClick={handleToggleLiveEvent}
            >
              <VoteIcon width={"16px"} height={"auto"} />
            </StreamingIconButton>
          </SPView>
        )}

        {showAnnouncement && (
          <SPView>
            <StreamingIconButton
              isDarkModeEnabled={isDarkModeEnabled}
              onClick={handleToggleAnnouncement}
            >
              <AnnouncementIcon
                style={{
                  width: "18px",
                  height: "auto",
                }}
              />
            </StreamingIconButton>
          </SPView>
        )}

        <a
          href={twitterLink}
          target="_blank"
          rel="noopener noreferrer"
          css={css`
            text-decoration: none;
          `}
        >
          <StreamingIconButtonWithText
            isDarkModeEnabled={isDarkModeEnabled}
            onClick={handleShareClick}
          >
            <TwitterIcon
              style={{
                width: "16px",
                height: "auto",
              }}
            />
            <Box
              display={["none", "block"]}
              fontSize={"12px"}
              lineHeight={"1em"}
            >
              ポストする
            </Box>
          </StreamingIconButtonWithText>
        </a>
      </Box>
    </Box>
  );
};

const DarkModeIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 18 18" {...props}>
      <path d="M9 6.75C10.2375 6.75 11.25 7.7625 11.25 9C11.25 10.2375 10.2375 11.25 9 11.25C7.7625 11.25 6.75 10.2375 6.75 9C6.75 7.7625 7.7625 6.75 9 6.75ZM9 5.25C6.93 5.25 5.25 6.93 5.25 9C5.25 11.07 6.93 12.75 9 12.75C11.07 12.75 12.75 11.07 12.75 9C12.75 6.93 11.07 5.25 9 5.25ZM1.5 9.75H3C3.4125 9.75 3.75 9.4125 3.75 9C3.75 8.5875 3.4125 8.25 3 8.25H1.5C1.0875 8.25 0.75 8.5875 0.75 9C0.75 9.4125 1.0875 9.75 1.5 9.75ZM15 9.75H16.5C16.9125 9.75 17.25 9.4125 17.25 9C17.25 8.5875 16.9125 8.25 16.5 8.25H15C14.5875 8.25 14.25 8.5875 14.25 9C14.25 9.4125 14.5875 9.75 15 9.75ZM8.25 1.5V3C8.25 3.4125 8.5875 3.75 9 3.75C9.4125 3.75 9.75 3.4125 9.75 3V1.5C9.75 1.0875 9.4125 0.75 9 0.75C8.5875 0.75 8.25 1.0875 8.25 1.5ZM8.25 15V16.5C8.25 16.9125 8.5875 17.25 9 17.25C9.4125 17.25 9.75 16.9125 9.75 16.5V15C9.75 14.5875 9.4125 14.25 9 14.25C8.5875 14.25 8.25 14.5875 8.25 15ZM4.4925 3.435C4.42311 3.36547 4.3407 3.31031 4.24997 3.27268C4.15924 3.23504 4.06198 3.21567 3.96375 3.21567C3.86552 3.21567 3.76826 3.23504 3.67753 3.27268C3.5868 3.31031 3.50439 3.36547 3.435 3.435C3.36547 3.50439 3.31031 3.5868 3.27268 3.67753C3.23504 3.76826 3.21567 3.86552 3.21567 3.96375C3.21567 4.06198 3.23504 4.15924 3.27268 4.24997C3.31031 4.3407 3.36547 4.42311 3.435 4.4925L4.23 5.2875C4.5225 5.58 5.0025 5.58 5.2875 5.2875C5.5725 4.995 5.58 4.515 5.2875 4.23L4.4925 3.435ZM13.77 12.7125C13.7006 12.643 13.6182 12.5878 13.5275 12.5502C13.4367 12.5125 13.3395 12.4932 13.2413 12.4932C13.143 12.4932 13.0458 12.5125 12.955 12.5502C12.8643 12.5878 12.7819 12.643 12.7125 12.7125C12.643 12.7819 12.5878 12.8643 12.5502 12.955C12.5125 13.0458 12.4932 13.143 12.4932 13.2413C12.4932 13.3395 12.5125 13.4367 12.5502 13.5275C12.5878 13.6182 12.643 13.7006 12.7125 13.77L13.5075 14.565C13.8 14.8575 14.28 14.8575 14.565 14.565C14.6345 14.4956 14.6897 14.4132 14.7273 14.3225C14.765 14.2317 14.7843 14.1345 14.7843 14.0363C14.7843 13.938 14.765 13.8408 14.7273 13.75C14.6897 13.6593 14.6345 13.5769 14.565 13.5075L13.77 12.7125ZM14.565 4.4925C14.6345 4.42311 14.6897 4.3407 14.7273 4.24997C14.765 4.15924 14.7843 4.06198 14.7843 3.96375C14.7843 3.86552 14.765 3.76826 14.7273 3.67753C14.6897 3.5868 14.6345 3.50439 14.565 3.435C14.4956 3.36547 14.4132 3.31031 14.3225 3.27268C14.2317 3.23504 14.1345 3.21567 14.0363 3.21567C13.938 3.21567 13.8408 3.23504 13.75 3.27268C13.6593 3.31031 13.5769 3.36547 13.5075 3.435L12.7125 4.23C12.42 4.5225 12.42 5.0025 12.7125 5.2875C13.005 5.5725 13.485 5.58 13.77 5.2875L14.565 4.4925ZM5.2875 13.77C5.35703 13.7006 5.41219 13.6182 5.44982 13.5275C5.48746 13.4367 5.50683 13.3395 5.50683 13.2413C5.50683 13.143 5.48746 13.0458 5.44982 12.955C5.41219 12.8643 5.35703 12.7819 5.2875 12.7125C5.21811 12.643 5.1357 12.5878 5.04497 12.5502C4.95424 12.5125 4.85698 12.4932 4.75875 12.4932C4.66052 12.4932 4.56326 12.5125 4.47253 12.5502C4.3818 12.5878 4.29939 12.643 4.23 12.7125L3.435 13.5075C3.1425 13.8 3.1425 14.28 3.435 14.565C3.7275 14.85 4.2075 14.8575 4.4925 14.565L5.2875 13.77Z" />
    </svg>
  );
};

const TwitterIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 1200 1227" {...props}>
      <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" />
    </svg>
  );
};
